import { formatBlogPostCtaLabel, resolveBlogPostLanguage } from "./blogPostLanguage";

type BlogArticleSeoInput = {
  slug: string;
  title: string;
  description: string;
  lang?: string;
  category: string;
};

export type BlogArticlePrimaryCta = {
  href: string;
  label: string;
};

export type BlogArticleSecondaryCta = BlogArticlePrimaryCta;

export type BlogEditorialCrossLink = {
  href: string;
  label: string;
  description: string;
};

export type BlogArticleDemoVideo = {
  kicker: string;
  title: string;
  description: string;
  bullets: string[];
  src: string;
  poster: string;
};

export type BlogArticleLiveDemo = {
  title: string;
  description: string;
  targetLanguageCode: string;
  instructionLanguageCode: string;
};

export const handwrittenExamPrepBlogSlugs = [
  "dutch-inburgering-speaking-practice",
  "dutch-naturalisation-language-requirement-speaking-plan",
  "cefr-a1-speaking-test-practice",
  "cefr-a2-speaking-test-practice",
  "ib-language-ab-initio-oral-practice",
  "nt2-dutch-speaking-practice",
  "a2-dutch-speaking-test-practice",
  "goethe-a1-speaking-practice",
  "goethe-b1-speaking-practice",
  "delf-a2-oral-practice",
  "dele-a2-oral-practice",
  "ielts-speaking-practice-with-ai",
  "cambridge-a2-speaking-practice",
  "ib-spanish-ab-initio-oral-practice",
  "ib-french-ab-initio-oral-practice",
] as const;

export const handwrittenAdultLearningBlogSlugs = [
  "adult-language-learning-speaking-practice",
  "learn-a-language-after-40-speaking-plan",
  "language-learning-for-busy-adults",
  "workplace-language-speaking-practice",
  "conversation-practice-for-shy-adults",
  "how-to-practice-speaking-alone-adult-language-learner",
] as const;

export const handwrittenBetweenLessonBlogSlugs = [
  "practise-french-between-lessons",
  "dutch-speaking-practice-after-class",
  "practise-german-after-class",
  "spanish-speaking-homework-after-class",
  "italian-speaking-practice-between-lessons",
  "business-english-practice-between-lessons-uk",
  "european-portuguese-practice-between-lessons",
  "polish-case-speaking-practice-between-lessons",
  "practise-japanese-speaking-between-lessons-without-freezing",
  "korean-speaking-practice-speech-levels-between-lessons",
  "mandarin-speaking-practice-after-tone-lessons",
  "understand-hindi-but-cannot-speak-between-lessons",
  "egyptian-arabic-speaking-practice-after-msa-class",
  "spoken-tamil-practice-after-written-tamil-lesson",
  "brazilian-portuguese-speaking-practice-after-class",
  "cantonese-speaking-practice-jyutping-to-conversation",
  "american-english-practice-between-esl-classes",
  "australian-english-listening-speaking-between-lessons",
  "indian-english-workplace-speaking-after-class",
  "latin-american-spanish-practice-between-private-lessons",
  "canadian-french-practice-between-language-classes",
  "msa-arabic-speaking-practice-after-a-lesson",
  "gulf-arabic-speaking-practice-with-a-private-tutor",
  "lebanese-arabic-heritage-practice-between-lessons",
] as const;

export const newHandwrittenBlogSlugs = [
  ...handwrittenExamPrepBlogSlugs,
  ...handwrittenAdultLearningBlogSlugs,
] as const;

const appStackBlogSlugs = [
  "korean-apps-from-zero",
  "danish-apps-from-zero",
  "japanese-apps-from-zero",
  "arabic-apps-from-zero",
  "russian-apps-from-zero",
  "turkish-apps-from-zero",
  "apps-to-improve-english",
  "mandarin-chinese-apps-from-zero",
  "german-apps-from-zero",
  "italian-apps-from-zero",
  "dutch-apps-from-zero",
  "spanish-apps-from-zero",
] as const;

const afterCourseBlogSlugs = [
  "what-to-do-after-pimsleur",
  "what-to-do-after-language-transfer",
  "what-to-do-after-assimil",
  "what-to-do-after-michel-thomas",
] as const;

const productDemoBlogSlugs = [
  ...newHandwrittenBlogSlugs,
  ...appStackBlogSlugs,
  ...afterCourseBlogSlugs,
  "clozemaster-alternative-fill-in-the-gap",
  "new-features-black-friday",
] as const;

const liveDemoOverrides: Record<string, BlogArticleLiveDemo> = {
  "practise-french-between-lessons": {
    title: "Try the French speaking step now",
    description:
      "Use the live tutor to say one idea from your last lesson, receive a focused correction, and repeat the improved sentence while it is still fresh.",
    targetLanguageCode: "fr-FR",
    instructionLanguageCode: "en-US",
  },
  "dutch-speaking-practice-after-class": {
    title: "Turn one Dutch class sentence into a live exchange",
    description:
      "Start with the Dutch sentence you have been avoiding. The live tutor can ask a small follow-up, correct the useful part, and let you try it again.",
    targetLanguageCode: "nl-NL",
    instructionLanguageCode: "en-US",
  },
  "practise-japanese-speaking-between-lessons-without-freezing": {
    title: "Practise the second Japanese sentence, not only the opener",
    description:
      "Use the live tutor to make an invitation, respond to a change, and repair the exchange instead of ending after one prepared line.",
    targetLanguageCode: "ja-JP",
    instructionLanguageCode: "en-US",
  },
  "korean-speaking-practice-speech-levels-between-lessons": {
    title: "Try the same Korean message with a different listener",
    description:
      "Use the live tutor to repeat one message for a friend and a colleague, then focus the correction on the speech level that changed.",
    targetLanguageCode: "ko-KR",
    instructionLanguageCode: "en-US",
  },
};

const primaryCtaOverrides: Record<string, BlogArticlePrimaryCta> = {
  "dutch-inburgering-speaking-practice": {
    href: "/practice/dutch-speaking",
    label: "Practice Dutch speaking",
  },
  "dutch-naturalisation-language-requirement-speaking-plan": {
    href: "/practice/dutch-speaking",
    label: "Practice Dutch speaking",
  },
  "nt2-dutch-speaking-practice": {
    href: "/practice/dutch-speaking",
    label: "Practice Dutch speaking",
  },
  "a2-dutch-speaking-test-practice": {
    href: "/practice/dutch-speaking",
    label: "Practice A2 Dutch",
  },
  "cefr-a1-speaking-test-practice": {
    href: "/practice/english-speaking",
    label: "Practice A1 speaking",
  },
  "cefr-a2-speaking-test-practice": {
    href: "/practice",
    label: "Browse A2 speaking practice",
  },
  "ielts-speaking-practice-with-ai": {
    href: "/practice/english-speaking",
    label: "Practice IELTS speaking",
  },
  "cambridge-a2-speaking-practice": {
    href: "/practice/english-speaking",
    label: "Practice Cambridge A2",
  },
  "goethe-a1-speaking-practice": {
    href: "/practice/german-speaking",
    label: "Practice German speaking",
  },
  "goethe-b1-speaking-practice": {
    href: "/practice/german-speaking",
    label: "Practice German speaking",
  },
  "delf-a2-oral-practice": {
    href: "/practice/french-speaking",
    label: "Practice French speaking",
  },
  "ib-french-ab-initio-oral-practice": {
    href: "/practice/french-speaking",
    label: "Practice French speaking",
  },
  "dele-a2-oral-practice": {
    href: "/practice/spanish-speaking",
    label: "Practice Spanish speaking",
  },
  "ib-spanish-ab-initio-oral-practice": {
    href: "/practice/spanish-speaking",
    label: "Practice Spanish speaking",
  },
  "ib-language-ab-initio-oral-practice": {
    href: "/practice",
    label: "Browse speaking practice",
  },
  "adult-language-learning-speaking-practice": {
    href: "/practice",
    label: "Browse speaking practice",
  },
  "learn-a-language-after-40-speaking-plan": {
    href: "/practice",
    label: "Start a speaking routine",
  },
  "language-learning-for-busy-adults": {
    href: "/practice",
    label: "Start a 15-minute session",
  },
  "workplace-language-speaking-practice": {
    href: "/practice/english-speaking",
    label: "Practice workplace speaking",
  },
  "conversation-practice-for-shy-adults": {
    href: "/practice",
    label: "Practice low-pressure speaking",
  },
  "how-to-practice-speaking-alone-adult-language-learner": {
    href: "/",
    label: "Practice speaking alone",
  },
};

const schemaTopicTerms: Record<string, string[]> = {
  "dutch-inburgering-speaking-practice": [
    "Dutch inburgering",
    "A2 Dutch speaking",
    "civic integration",
    "Dutch speaking practice",
  ],
  "dutch-naturalisation-language-requirement-speaking-plan": [
    "Dutch naturalisation",
    "integration requirement",
    "A2 Dutch speaking",
    "Dutch speaking practice",
  ],
  "cefr-a1-speaking-test-practice": ["CEFR A1", "A1 speaking test", "beginner speaking practice"],
  "cefr-a2-speaking-test-practice": ["CEFR A2", "A2 speaking test", "roleplay speaking practice"],
  "ib-language-ab-initio-oral-practice": ["IB ab initio", "oral practice", "language acquisition"],
  "nt2-dutch-speaking-practice": ["NT2 Dutch", "Dutch conversation", "Dutch speaking practice"],
  "a2-dutch-speaking-test-practice": ["A2 Dutch", "Dutch speaking test", "Dutch roleplay practice"],
  "goethe-a1-speaking-practice": ["Goethe A1", "German speaking", "A1 German"],
  "goethe-b1-speaking-practice": ["Goethe B1", "German speaking", "B1 German"],
  "delf-a2-oral-practice": ["DELF A2", "French oral practice", "A2 French"],
  "dele-a2-oral-practice": ["DELE A2", "Spanish oral practice", "A2 Spanish"],
  "ielts-speaking-practice-with-ai": ["IELTS speaking", "English speaking", "AI speaking practice"],
  "cambridge-a2-speaking-practice": ["Cambridge A2", "English speaking", "A2 speaking practice"],
  "ib-spanish-ab-initio-oral-practice": [
    "IB Spanish ab initio",
    "Spanish oral practice",
    "IB speaking",
  ],
  "ib-french-ab-initio-oral-practice": [
    "IB French ab initio",
    "French oral practice",
    "IB speaking",
  ],
  "adult-language-learning-speaking-practice": [
    "adult language learning",
    "speaking practice",
    "conversation confidence",
  ],
  "learn-a-language-after-40-speaking-plan": [
    "learn a language after 40",
    "adult learners",
    "speaking routine",
  ],
  "language-learning-for-busy-adults": [
    "busy adult learners",
    "15-minute language routine",
    "speaking practice",
  ],
  "workplace-language-speaking-practice": [
    "workplace language",
    "professional speaking",
    "adult language learning",
  ],
  "conversation-practice-for-shy-adults": [
    "shy adult learners",
    "conversation practice",
    "speaking confidence",
  ],
  "how-to-practice-speaking-alone-adult-language-learner": [
    "speaking alone",
    "adult language learner",
    "self-talk practice",
  ],
};

const editorialCrossLinks: Record<string, BlogEditorialCrossLink[]> = {
  "dutch-inburgering-speaking-practice": [
    {
      href: "/blog/a2-dutch-speaking-test-practice",
      label: "A2 Dutch speaking test practice",
      description: "Use the same answer shapes when the prompt changes.",
    },
    {
      href: "/blog/nt2-dutch-speaking-practice",
      label: "NT2 Dutch speaking practice",
      description: "Move from exam-style Dutch into real daily conversations.",
    },
  ],
  "dutch-naturalisation-language-requirement-speaking-plan": [
    {
      href: "/blog/dutch-inburgering-speaking-practice",
      label: "Dutch inburgering speaking practice",
      description: "Build a daily A2 loop around appointments, work, and official tasks.",
    },
    {
      href: "/blog/a2-dutch-speaking-test-practice",
      label: "A2 Dutch speaking test practice",
      description: "Keep answers short, complete, and flexible under pressure.",
    },
  ],
  "cefr-a1-speaking-test-practice": [
    {
      href: "/blog/cefr-a2-speaking-test-practice",
      label: "CEFR A2 speaking practice",
      description: "Step up from short answers into everyday roleplay turns.",
    },
    {
      href: "/blog/conversation-practice-for-shy-adults",
      label: "Conversation practice for shy adults",
      description: "Reduce the pressure before speaking with a real person.",
    },
  ],
  "cefr-a2-speaking-test-practice": [
    {
      href: "/blog/cefr-a1-speaking-test-practice",
      label: "CEFR A1 speaking test practice",
      description: "Review the simpler answer shapes that A2 still depends on.",
    },
    {
      href: "/blog/language-learning-for-busy-adults",
      label: "Language learning for busy adults",
      description: "Turn A2 practice into a routine you can actually keep.",
    },
  ],
  "ib-language-ab-initio-oral-practice": [
    {
      href: "/blog/ib-spanish-ab-initio-oral-practice",
      label: "IB Spanish ab initio oral practice",
      description: "See the same method applied to Spanish image prompts.",
    },
    {
      href: "/blog/ib-french-ab-initio-oral-practice",
      label: "IB French ab initio oral practice",
      description: "Build flexible French answers without memorizing a speech.",
    },
  ],
  "nt2-dutch-speaking-practice": [
    {
      href: "/blog/dutch-inburgering-speaking-practice",
      label: "Dutch inburgering speaking practice",
      description: "Use practical A2 scenes when civic integration is the goal.",
    },
    {
      href: "/blog/a2-dutch-speaking-test-practice",
      label: "A2 Dutch speaking test practice",
      description: "Practice short complete answers before adding pressure.",
    },
  ],
  "a2-dutch-speaking-test-practice": [
    {
      href: "/blog/dutch-inburgering-speaking-practice",
      label: "Dutch inburgering speaking practice",
      description: "Apply A2 answer shapes to civic-integration situations.",
    },
    {
      href: "/blog/nt2-dutch-speaking-practice",
      label: "NT2 Dutch speaking practice",
      description: "Make test Dutch transfer to work, school, and appointments.",
    },
  ],
  "goethe-a1-speaking-practice": [
    {
      href: "/blog/goethe-b1-speaking-practice",
      label: "Goethe B1 speaking practice",
      description: "See how the same correction loop scales to longer answers.",
    },
    {
      href: "/blog/cefr-a1-speaking-test-practice",
      label: "CEFR A1 speaking test practice",
      description: "Use simple answer frames across beginner speaking tests.",
    },
  ],
  "goethe-b1-speaking-practice": [
    {
      href: "/blog/goethe-a1-speaking-practice",
      label: "Goethe A1 speaking practice",
      description: "Strengthen the simple German phrases B1 answers build on.",
    },
    {
      href: "/blog/workplace-language-speaking-practice",
      label: "Workplace language speaking practice",
      description: "Practice opinions and explanations in adult work situations.",
    },
  ],
  "delf-a2-oral-practice": [
    {
      href: "/blog/ib-french-ab-initio-oral-practice",
      label: "IB French ab initio oral practice",
      description: "Use flexible French answers for image and theme prompts.",
    },
    {
      href: "/blog/cefr-a2-speaking-test-practice",
      label: "CEFR A2 speaking practice",
      description: "Review the A2 roleplay routine behind DELF practice.",
    },
  ],
  "dele-a2-oral-practice": [
    {
      href: "/blog/ib-spanish-ab-initio-oral-practice",
      label: "IB Spanish ab initio oral practice",
      description: "Practice Spanish theme answers beyond one memorized script.",
    },
    {
      href: "/blog/cefr-a2-speaking-test-practice",
      label: "CEFR A2 speaking practice",
      description: "Use the same everyday-roleplay structure for A2 speaking.",
    },
  ],
  "ielts-speaking-practice-with-ai": [
    {
      href: "/blog/workplace-language-speaking-practice",
      label: "Workplace language speaking practice",
      description: "Build adult English answers for meetings and professional situations.",
    },
    {
      href: "/blog/conversation-practice-for-shy-adults",
      label: "Conversation practice for shy adults",
      description: "Practice follow-up answers without freezing.",
    },
  ],
  "cambridge-a2-speaking-practice": [
    {
      href: "/blog/cefr-a2-speaking-test-practice",
      label: "CEFR A2 speaking practice",
      description: "Use A2 answer shapes for broader speaking-test practice.",
    },
    {
      href: "/blog/language-learning-for-busy-adults",
      label: "Language learning for busy adults",
      description: "Keep short speaking drills alive during a full week.",
    },
  ],
  "ib-spanish-ab-initio-oral-practice": [
    {
      href: "/blog/ib-language-ab-initio-oral-practice",
      label: "IB language ab initio oral practice",
      description: "Review the general oral method before Spanish-specific drilling.",
    },
    {
      href: "/blog/dele-a2-oral-practice",
      label: "DELE A2 oral practice",
      description: "Use Spanish roleplay prompts to strengthen spoken flexibility.",
    },
  ],
  "ib-french-ab-initio-oral-practice": [
    {
      href: "/blog/ib-language-ab-initio-oral-practice",
      label: "IB language ab initio oral practice",
      description: "Review the general oral method before French-specific drilling.",
    },
    {
      href: "/blog/delf-a2-oral-practice",
      label: "DELF A2 oral practice",
      description: "Use French roleplay prompts to strengthen spoken flexibility.",
    },
  ],
  "adult-language-learning-speaking-practice": [
    {
      href: "/blog/learn-a-language-after-40-speaking-plan",
      label: "Learn a language after 40",
      description: "Build a speaking plan that respects adult time and confidence.",
    },
    {
      href: "/blog/language-learning-for-busy-adults",
      label: "Language learning for busy adults",
      description: "Shrink the same speaking loop into a realistic weekly routine.",
    },
  ],
  "learn-a-language-after-40-speaking-plan": [
    {
      href: "/blog/adult-language-learning-speaking-practice",
      label: "Adult language learning speaking practice",
      description: "Start with real-life situations before chasing fluency.",
    },
    {
      href: "/blog/how-to-practice-speaking-alone-adult-language-learner",
      label: "How adults can practice speaking alone",
      description: "Get useful spoken reps when no conversation partner is available.",
    },
  ],
  "language-learning-for-busy-adults": [
    {
      href: "/blog/adult-language-learning-speaking-practice",
      label: "Adult language learning speaking practice",
      description: "Keep the routine connected to real adult conversations.",
    },
    {
      href: "/blog/how-to-practice-speaking-alone-adult-language-learner",
      label: "How adults can practice speaking alone",
      description: "Use solo practice when the week leaves no room for scheduling.",
    },
  ],
  "workplace-language-speaking-practice": [
    {
      href: "/blog/adult-language-learning-speaking-practice",
      label: "Adult language learning speaking practice",
      description: "Build confidence for the broader adult situations around work.",
    },
    {
      href: "/blog/ielts-speaking-practice-with-ai",
      label: "IELTS speaking practice with AI",
      description: "Practice longer English answers and follow-up questions.",
    },
  ],
  "conversation-practice-for-shy-adults": [
    {
      href: "/blog/how-to-practice-speaking-alone-adult-language-learner",
      label: "How adults can practice speaking alone",
      description: "Start privately before moving into live conversation.",
    },
    {
      href: "/blog/adult-language-learning-speaking-practice",
      label: "Adult language learning speaking practice",
      description: "Turn low-pressure practice into real-life speaking moments.",
    },
  ],
  "how-to-practice-speaking-alone-adult-language-learner": [
    {
      href: "/blog/conversation-practice-for-shy-adults",
      label: "Conversation practice for shy adults",
      description: "Move from solo reps into safer live conversation practice.",
    },
    {
      href: "/blog/language-learning-for-busy-adults",
      label: "Language learning for busy adults",
      description: "Fit solo speaking into a short weekly routine.",
    },
  ],
};

export function isHandwrittenExamPrepBlogSlug(slug: string): boolean {
  return handwrittenExamPrepBlogSlugs.includes(
    slug as (typeof handwrittenExamPrepBlogSlugs)[number]
  );
}

export function isHandwrittenAdultLearningBlogSlug(slug: string): boolean {
  return handwrittenAdultLearningBlogSlugs.includes(
    slug as (typeof handwrittenAdultLearningBlogSlugs)[number]
  );
}

export function isHandwrittenBetweenLessonBlogSlug(slug: string): boolean {
  return handwrittenBetweenLessonBlogSlugs.includes(
    slug as (typeof handwrittenBetweenLessonBlogSlugs)[number]
  );
}

export function resolveBlogArticlePrimaryCta(post: BlogArticleSeoInput): BlogArticlePrimaryCta {
  const override = primaryCtaOverrides[post.slug];
  if (override) {
    return override;
  }

  if (isHandwrittenBetweenLessonBlogSlug(post.slug)) {
    return {
      href: "/",
      label: "Start speaking practice",
    };
  }

  return {
    href: "/",
    label: formatBlogPostCtaLabel(post),
  };
}

export function resolveBlogArticleSecondaryCta(
  post: BlogArticleSeoInput
): BlogArticleSecondaryCta | undefined {
  if (!isHandwrittenBetweenLessonBlogSlug(post.slug)) return undefined;

  return {
    href: "/between-lessons/lesson-materials",
    label: "Use your last lesson",
  };
}

export function resolveBlogArticleSchemaDetails(post: BlogArticleSeoInput) {
  const displayLanguage = resolveBlogPostLanguage(post);
  const clusterTerms =
    schemaTopicTerms[post.slug] ??
    (isHandwrittenBetweenLessonBlogSlug(post.slug)
      ? ["between-lesson practice", "speaking homework", "human teacher plus AI tutor"]
      : []);
  const languageTerms = displayLanguage === "All Languages" ? [] : [displayLanguage];
  const categoryTerm = post.category.replace(/-/g, " ");
  const keywords = Array.from(new Set([...clusterTerms, ...languageTerms, categoryTerm]));

  const articleSection = isHandwrittenExamPrepBlogSlug(post.slug)
    ? "Language exam preparation"
    : isHandwrittenAdultLearningBlogSlug(post.slug)
      ? "Adult language learning"
      : isHandwrittenBetweenLessonBlogSlug(post.slug)
        ? "Between-lesson language practice"
        : categoryTerm;

  return {
    articleSection,
    keywords,
    about: keywords.slice(0, 6).map((name) => ({
      "@type": "Thing",
      name,
    })),
  };
}

export function resolveBlogEditorialCrossLinks(slug: string): BlogEditorialCrossLink[] {
  const explicitLinks = editorialCrossLinks[slug];
  if (explicitLinks) return explicitLinks;

  if (isHandwrittenBetweenLessonBlogSlug(slug)) {
    return [
      {
        href: "/between-lessons/lesson-materials",
        label: "The complete between-lesson workflow",
        description:
          "Turn one class topic into a three-day or seven-day speaking cycle and a useful teacher brief.",
      },
      {
        href: "/tutors",
        label: "Use ChickyTutor with a human tutor",
        description:
          "See how teacher direction and private speaking repetitions can support each other.",
      },
    ];
  }

  return [];
}

export function resolveBlogArticleLiveDemo(slug: string): BlogArticleLiveDemo | undefined {
  return liveDemoOverrides[slug];
}

export function resolveBlogArticleDemoVideo(slug: string): BlogArticleDemoVideo | undefined {
  if (!productDemoBlogSlugs.includes(slug as (typeof productDemoBlogSlugs)[number])) {
    return undefined;
  }

  const baseVideo = {
    src: "/ui/chickytutor-how-it-works4.mp4",
    poster: "/ui/chickytutor-how-it-works4-thumbnail.jpg",
  };

  if (isHandwrittenExamPrepBlogSlug(slug)) {
    return {
      ...baseVideo,
      kicker: "Watch the speaking loop",
      title: "See how to practice before the speaking test",
      description:
        "The useful pattern is simple: answer one prompt out loud, get one correction, repeat the better sentence, then handle a small follow-up.",
      bullets: [
        "Practice oral prompts without waiting for a teacher or partner.",
        "Turn one mistake into a sentence you can say again.",
        "Add a follow-up so the answer is flexible, not memorized.",
      ],
    };
  }

  if (isHandwrittenAdultLearningBlogSlug(slug)) {
    return {
      ...baseVideo,
      kicker: "Watch low-pressure practice",
      title: "See how adults can turn private practice into speech",
      description:
        "The demo shows the part adult learners often miss: saying the sentence, hearing a correction, and trying again before a real conversation.",
      bullets: [
        "Start with one practical situation instead of a giant fluency goal.",
        "Practice privately when a real conversation would feel too exposed.",
        "Repeat the corrected version so the better phrase is ready later.",
      ],
    };
  }

  if (afterCourseBlogSlugs.includes(slug as (typeof afterCourseBlogSlugs)[number])) {
    return {
      ...baseVideo,
      kicker: "Watch the next step after study",
      title: "See how ChickyTutor turns course knowledge into speaking",
      description:
        "After an audio course, book course, or grammar method, the missing step is usually live output. This demo shows that output loop.",
      bullets: [
        "Use what you learned in a real spoken reply.",
        "Get corrected without stopping the conversation.",
        "Answer a related follow-up so the phrase is not locked to one script.",
      ],
    };
  }

  if (slug === "clozemaster-alternative-fill-in-the-gap") {
    return {
      ...baseVideo,
      kicker: "Watch the output layer",
      title: "See the next step after vocabulary practice",
      description:
        "Fill-in-the-gap practice helps recall. ChickyTutor adds the speaking layer where remembered words become full answers.",
      bullets: [
        "Move from recognizing a sentence to saying one.",
        "Use vocabulary in a conversation instead of only an exercise.",
        "Repeat a corrected phrase so the word is easier to retrieve later.",
      ],
    };
  }

  return {
    ...baseVideo,
    kicker: "Watch ChickyTutor in action",
    title: "See how the AI speaking tutor works",
    description:
      "The demo shows the product loop behind the article: speak, get a useful correction, repeat the better version, and keep going.",
    bullets: [
      "Start with a real spoken answer.",
      "Get feedback on the sentence you actually said.",
      "Try again immediately while the correction is still fresh.",
    ],
  };
}
